import { Chart } from 'react-chartjs-2'

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend)

const BAR_COLORS = ['#6ECBFA', '#EAF200', '#FFB800', '#8B5CF6', '#EC4899']
const LINE_COLORS = ['#FFB800', '#EAF200', '#8B5CF6', '#EC4899']

interface Props {
  labels: string[]
  lineItems: {
    title: string
    values: (string | number)[]
  }[]
  barItems: {
    title: string
    values: (string | number)[]
  }[]
}

const BarAndLineChart = ({ labels, lineItems, barItems }: Props) => {
  const datasets = [
    ...barItems.map((item, i) => ({
      type: 'bar' as const,
      label: item.title,
      data: item.values,
      backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
      borderRadius: 2,
      borderSkipped: false,
      yAxisID: 'y',
      order: 2,
      datalabels: { display: false }
    })),
    ...lineItems.map((item, i) => ({
      type: 'line' as const,
      label: item.title,
      data: item.values,
      borderColor: LINE_COLORS[i % LINE_COLORS.length],
      backgroundColor: LINE_COLORS[i % LINE_COLORS.length],
      pointBackgroundColor: LINE_COLORS[i % LINE_COLORS.length],
      pointBorderColor: LINE_COLORS[i % LINE_COLORS.length],
      pointRadius: 4,
      borderWidth: 2,
      tension: 0.3,
      yAxisID: 'y1',
      order: 1,
      datalabels: {
        display: true,
        color: '#34414F',
        backgroundColor: LINE_COLORS[i % LINE_COLORS.length],
        borderRadius: 3,
        padding: { top: 2, bottom: 2, left: 4, right: 4 },
        font: { size: 10 },
        anchor: 'end' as const,
        align: 'top' as const,
        formatter: (value: string | number) => {
          if (typeof value === 'string') return value
          return `${value}%`
        }
      }
    }))
  ]

  return (
    <Chart
      type='bar'
      data={{ labels, datasets }}
      plugins={[ChartDataLabels]}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 20 } },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              color: '#34414F',
              font: { size: 12 },
              padding: 8,
              usePointStyle: true,
              pointStyleWidth: 24,
              generateLabels: (chart) =>
                chart.data.datasets.map((dataset, i) => {
                  const isLine = dataset.type === 'line'
                  return {
                    text: dataset.label ?? '',
                    fillStyle: isLine ? 'transparent' : (dataset.backgroundColor as string),
                    strokeStyle: dataset.borderColor as string,
                    lineWidth: isLine ? 2 : 0,
                    pointStyle: isLine ? ('line' as const) : ('rect' as const),
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i
                  }
                })
            }
          },
          tooltip: {
            backgroundColor: 'rgba(2, 6, 23, 0.95)',
            titleFont: { family: "'JetBrains Mono', monospace", size: 11, weight: 'bold' },
            bodyFont: { family: "'JetBrains Mono', monospace", size: 11 },
            padding: 8,
            cornerRadius: 4,
            borderColor: 'rgba(6,182,212,0.2)',
            borderWidth: 1,
            displayColors: true
          }
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 10 }, color: '#34414F', maxRotation: 0 }
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: '#E4E8EE' },
            ticks: { font: { size: 10 }, color: '#34414F', padding: 6, count: 6 }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            border: { display: false },
            grid: { drawOnChartArea: false },
            ticks: {
              font: { size: 10 },
              color: '#34414F',
              padding: 6,
              count: 6,
              callback: (value) => `${value}%`
            }
          }
        }
      }}
    />
  )
}

export default BarAndLineChart
